import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  secondary: '#0891b2',
  dark: '#0f172a',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc'
}

const NavbarContainer = styled(motion.nav)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: ${props => props.scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.95)'};
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.scrolled ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 1px 0 rgba(0, 0, 0, 0.05)'};
  z-index: 1000;
  transition: all 0.3s ease;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${props => props.scrolled ? '64px' : '72px'};
  transition: height 0.3s ease;

  @media (max-width: 768px) {
    padding: 0 16px;
    height: 60px;
  }
`

const Logo = styled(motion.img)`
  height: 50px;
  cursor: pointer;

  @media (max-width: 768px) {
    height: 42px;
  }
`

const NavLinksDesktop = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;

  @media (max-width: 900px) {
    display: none;
  }
`

const NavLink = styled(motion.div)`
  font-size: 15px;
  font-weight: 500;
  color: ${props => props.active ? colors.primary : colors.text};
  cursor: pointer;
  position: relative;
  padding: 8px 0;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: ${props => props.active ? '100%' : '0'};
    height: 2px;
    background: linear-gradient(90deg, ${colors.primary}, ${colors.secondary});
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${colors.primary};

    &::after {
      width: 100%;
    }
  }
`

const DropdownContainer = styled.div`
  position: relative;
`

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 16px;
  background: ${colors.white};
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  padding: 8px 0;
  z-index: 1001;
  border: 1px solid rgba(0, 0, 0, 0.05);

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 12px;
    height: 12px;
    background: ${colors.white};
    border-left: 1px solid rgba(0, 0, 0, 0.05);
    border-top: 1px solid rgba(0, 0, 0, 0.05);
  }
`

const DropdownItem = styled(motion.div)`
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.text};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: ${colors.background};
    color: ${colors.primary};
    padding-left: 24px;
  }
`

const ContactButton = styled(motion.button)`
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 10px 24px;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(13, 148, 136, 0.3);
  }
`

const MenuToggle = styled(motion.button)`
  display: none;
  background: none;
  border: none;
  width: 40px;
  height: 40px;
  cursor: pointer;
  position: relative;
  border-radius: 8px;

  &:hover {
    background: ${colors.background};
  }

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const MenuIcon = styled.div`
  width: 20px;
  height: 14px;
  position: relative;

  span {
    position: absolute;
    left: 0;
    width: 100%;
    height: 2px;
    background: ${colors.dark};
    border-radius: 2px;
    transition: all 0.3s ease;

    &:nth-child(1) {
      top: ${props => props.isOpen ? '6px' : '0'};
      transform: ${props => props.isOpen ? 'rotate(45deg)' : 'rotate(0)'};
    }

    &:nth-child(2) {
      top: 6px;
      opacity: ${props => props.isOpen ? '0' : '1'};
    }

    &:nth-child(3) {
      top: ${props => props.isOpen ? '6px' : '12px'};
      transform: ${props => props.isOpen ? 'rotate(-45deg)' : 'rotate(0)'};
    }
  }
`

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 60px;
  left: 0;
  width: 100%;
  background: ${colors.white};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  padding: 16px;
  z-index: 999;
  max-height: calc(100vh - 60px);
  overflow-y: auto;

  @media (min-width: 901px) {
    display: none;
  }
`

const MobileNavLink = styled(motion.div)`
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.active ? colors.primary : colors.text};
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background};
    color: ${colors.primary};
  }
`

const MobileDropdownContainer = styled.div`
  margin-bottom: 8px;
`

const MobileDropdownHeader = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: ${colors.text};
  padding: 16px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background};
    color: ${colors.primary};
  }

  span {
    font-size: 12px;
    transition: transform 0.3s ease;
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  }
`

const MobileDropdownItems = styled(motion.div)`
  overflow: hidden;
  background: ${colors.background};
  border-radius: 8px;
  margin: 4px 0 8px;
`

const MobileDropdownItem = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: ${colors.textLight};
  padding: 14px 24px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(13, 148, 136, 0.1);
    color: ${colors.primary};
  }
`

const MobileContactButton = styled(motion.button)`
  width: 100%;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 16px;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 16px;
`

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false)
  const [mobileProductsDropdownOpen, setMobileProductsDropdownOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location])

  const handleNavigation = (path) => {
    navigate(path)
    setMobileMenuOpen(false)
    setProductsDropdownOpen(false)
    setMobileProductsDropdownOpen(false)
  }

  const isActive = (path) => location.pathname === path

  const products = [
    { name: 'eMedicarematch', path: '/products/emedicarematch' },
    { name: 'ZenoHire', path: '/products/zenohire' },
    { name: 'ZenoPolicy', path: '/products/zenopolicy' },
    { name: 'AI Caregiving', path: '/products/aicaregiving' },
    { name: 'Azentris', path: '/products/azentris' }
  ]

  return (
    <>
      <NavbarContainer
        scrolled={scrolled}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <MaxWidthContainer scrolled={scrolled}>
          <Logo
            src="/rsng-logo.png"
            alt="RSNG INFO Solutions"
            onClick={() => handleNavigation('/')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          />

          <NavLinksDesktop>
            <NavLink
              active={isActive('/')}
              onClick={() => handleNavigation('/')}
              whileHover={{ y: -1 }}
            >
              Home
            </NavLink>
            <NavLink
              active={isActive('/services')}
              onClick={() => handleNavigation('/services')}
              whileHover={{ y: -1 }}
            >
              Services
            </NavLink>
            <NavLink
              active={isActive('/careers')}
              onClick={() => handleNavigation('/careers')}
              whileHover={{ y: -1 }}
            >
              Careers
            </NavLink>
            <NavLink
              active={isActive('/case-studies')}
              onClick={() => handleNavigation('/case-studies')}
              whileHover={{ y: -1 }}
            >
              Case Studies
            </NavLink>
            <DropdownContainer
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <NavLink
                active={location.pathname.includes('/products')}
                whileHover={{ y: -1 }}
              >
                Products
                <motion.span
                  animate={{ rotate: productsDropdownOpen ? 180 : 0 }}
                  style={{ display: 'inline-block', marginLeft: 4, fontSize: 10 }}
                >
                  ▼
                </motion.span>
              </NavLink>
              <AnimatePresence>
                {productsDropdownOpen && (
                  <DropdownMenu
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {products.map((product, index) => (
                      <DropdownItem
                        key={product.path}
                        onClick={() => handleNavigation(product.path)}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        {product.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                )}
              </AnimatePresence>
            </DropdownContainer>
            <NavLink
              active={isActive('/about')}
              onClick={() => handleNavigation('/about')}
              whileHover={{ y: -1 }}
            >
              About
            </NavLink>
            <NavLink
              active={isActive('/contact')}
              onClick={() => handleNavigation('/contact')}
              whileHover={{ y: -1 }}
            >
              Contact Us
            </NavLink>
          </NavLinksDesktop>

          <MenuToggle
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            <MenuIcon isOpen={mobileMenuOpen}>
              <span />
              <span />
              <span />
            </MenuIcon>
          </MenuToggle>
        </MaxWidthContainer>
      </NavbarContainer>

      <AnimatePresence>
        {mobileMenuOpen && (
          <MobileMenu
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            <MobileNavLink
              active={isActive('/')}
              onClick={() => handleNavigation('/')}
            >
              Home
            </MobileNavLink>
            <MobileNavLink
              active={isActive('/services')}
              onClick={() => handleNavigation('/services')}
            >
              Services
            </MobileNavLink>
            <MobileNavLink
              active={isActive('/careers')}
              onClick={() => handleNavigation('/careers')}
            >
              Careers
            </MobileNavLink>
            <MobileNavLink
              active={isActive('/case-studies')}
              onClick={() => handleNavigation('/case-studies')}
            >
              Case Studies
            </MobileNavLink>

            <MobileDropdownContainer>
              <MobileDropdownHeader
                isOpen={mobileProductsDropdownOpen}
                onClick={() => setMobileProductsDropdownOpen(!mobileProductsDropdownOpen)}
              >
                Products
                <span>▼</span>
              </MobileDropdownHeader>
              <AnimatePresence>
                {mobileProductsDropdownOpen && (
                  <MobileDropdownItems
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {products.map((product) => (
                      <MobileDropdownItem
                        key={product.path}
                        onClick={() => handleNavigation(product.path)}
                      >
                        {product.name}
                      </MobileDropdownItem>
                    ))}
                  </MobileDropdownItems>
                )}
              </AnimatePresence>
            </MobileDropdownContainer>

            <MobileNavLink
              active={isActive('/about')}
              onClick={() => handleNavigation('/about')}
            >
              About Us
            </MobileNavLink>

            <MobileNavLink
              active={isActive('/contact')}
              onClick={() => handleNavigation('/contact')}
            >
              Contact Us
            </MobileNavLink>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  )
}

export default Navbar
